import { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import TaskSkeleton from "./TaskSkeleton";
import "./ExpenseModule.css";

export default function ExpenseHeadList() {
    const { http, user } = AuthUser();
    const [heads, setHeads] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [name, setName] = useState("");
    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState("");

    useEffect(() => { fetchHeads(); }, []);

    const fetchHeads = async () => {
        setIsLoading(true);
        try {
            const res = await http.get(`/expense_heads?login_user=${user.id}`);
            if (res.data.success) setHeads(res.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Error fetching expense heads");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name.trim()) { toast.error("Please enter a name"); return; }

        const duplicate = heads.some(
            (head) => (head.name || "").trim().toLowerCase() === name.trim().toLowerCase()
        );
        if (duplicate) {
            toast.error("Expense head name already exists");
            return;
        }

        try {
            const res = await http.post(`/expense_head?login_user=${user.id}`, {
                name: name.trim(),
            });
            if (res.data.success) {
                toast.success("Expense head added!");
                setName("");
                fetchHeads();
            } else {
                toast.error(res.data.message || "Error adding expense head");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error adding expense head");
        }
    };

    const handleUpdate = async (id) => {
        if (!editName.trim()) { toast.error("Name cannot be empty"); return; }

        const duplicate = heads.some(
            (head) =>
                head.id !== id
                && (head.name || "").trim().toLowerCase() === editName.trim().toLowerCase()
        );
        if (duplicate) {
            toast.error("Expense head name already exists");
            return;
        }

        try {
            const res = await http.put(`/expense_head/${id}`, {
                name: editName.trim(),
            });
            if (res.data.success) {
                toast.success("Updated!");
                setEditId(null);
                fetchHeads();
            } else {
                toast.error(res.data.message || "Error updating");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error updating");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this expense head?")) return;
        try {
            const res = await http.delete(`/expense_head/${id}`);
            if (res.data.success) {
                toast.success("Deleted!");
                fetchHeads();
            }
        } catch (err) {
            toast.error("Error deleting");
        }
    };

    return (
        <>
            <Toaster />
            <div className="container mt-3 expense-module">
                <h5 className="fw-bold mb-3">Expense Head</h5>

                <form onSubmit={handleAdd} className="row g-2 mb-3">
                    <div className="col-md-4">
                        <input type="text" className="form-control form-control-sm" placeholder="Expense head name (e.g. Grocery)" value={name} onChange={(e) => setName(e.target.value)} required />
                    </div>
                    <div className="col-auto">
                        <button type="submit" className="btn btn-sm btn-success px-3"><i className="fas fa-plus me-1"></i>Add</button>
                    </div>
                </form>

                {isLoading ? <TaskSkeleton /> : (
                    <table className="table table-striped table-sm">
                        <thead className="table-primary">
                            <tr>
                                <th style={{ width: "50px" }}>No</th>
                                <th>Name</th>
                                <th style={{ width: "180px" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {heads.length === 0 ? (
                                <tr><td colSpan={3} className="text-center text-muted py-3">No expense heads. Add one above.</td></tr>
                            ) : heads.map((head, idx) => (
                                <tr key={head.id}>
                                    <td>{idx + 1}</td>
                                    <td>
                                        {editId === head.id ? (
                                            <input type="text" className="form-control form-control-sm" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                                        ) : head.name}
                                    </td>
                                    <td>
                                        {editId === head.id ? (
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-primary px-2" onClick={() => handleUpdate(head.id)}><i className="fas fa-check"></i></button>
                                                <button className="btn btn-sm btn-secondary px-2" onClick={() => setEditId(null)}><i className="fas fa-times"></i></button>
                                            </div>
                                        ) : (
                                            <div className="d-flex gap-1">
                                                <button className="btn btn-sm btn-outline-primary px-2" onClick={() => { setEditId(head.id); setEditName(head.name); }}><i className="fas fa-edit"></i></button>
                                                <button className="btn btn-sm btn-outline-danger px-2" onClick={() => handleDelete(head.id)}><i className="fas fa-trash"></i></button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
}
